import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/Common/Breadcrumb";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useGetMyOrganization,
  useGetOrganizationMembers,
} from "@/hooks/useOrganizations";
import { FREE_PLAN_NAME } from "@/lib/constants";
import { Building2, CreditCard, Github, Plus, Zap } from "lucide-react";
import { useMemo } from "react";

export function OrganizationsPage() {
  const orgQuery = useGetMyOrganization();
  const orgMembersQuery = useGetOrganizationMembers();
  const currentPlan = orgQuery.data.plan;

  const breadcrumbs = useMemo(() => {
    return [{ label: "organizations" }];
  }, []);

  return (
    <div className="min-h-full w-1/2">
      <div className="hidden lg:block border-b border-slate-800 bg-slate-900/90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5">
          <Breadcrumb items={breadcrumbs} />
        </div>
      </div>
      <div className="p-8 space-y-8">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-cyan-400  mb-2">
              Organization
            </h1>
          </div>
          <Button
            className="cyber-button"
            disabled={currentPlan.name === FREE_PLAN_NAME}
          >
            <Plus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        </div>

        <Tabs defaultValue="overview" className="space-y-8">
          <TabsList className="bg-slate-800/50 border border-cyan-500/30 p-1">
            <TabsTrigger
              value="overview"
              className=" data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="members"
              className=" data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 ml-2"
            >
              Members
            </TabsTrigger>
            {/* <TabsTrigger
              value="settings"
              className=" data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 ml-2"
            >
              Settings
            </TabsTrigger> */}
          </TabsList>

          <TabsContent value="overview" className="space-y-8">
            {/* Organization Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="holo-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-medium text-slate-400 ">
                    Organization
                  </CardTitle>
                  <Building2 className="h-5 w-5 text-cyan-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold text-cyan-100  truncate">
                    {orgQuery.data.name}
                  </div>
                  <p className="text-xs text-slate-400  mt-1 truncate">
                    {orgQuery.data.email}
                  </p>
                </CardContent>
              </Card>

              <Card className="holo-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-medium text-slate-400 ">
                    Plan
                  </CardTitle>
                  <Zap className="h-5 w-5 text-yellow-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold text-cyan-100 ">
                    {currentPlan?.name}
                  </div>
                  <p className="text-xs text-slate-400  mt-1">
                    ${(currentPlan?.costPerUserCents || 0) / 100}/month
                  </p>
                </CardContent>
              </Card>

              <Card className="holo-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-medium text-slate-400 ">
                    Credits
                  </CardTitle>
                  <CreditCard className="h-5 w-5 text-green-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold text-green-400 ">
                    {orgQuery.data.credits
                      .reduce((sum, credit) => sum + credit.amountCents + 0, 0)
                      .toLocaleString()}
                  </div>
                  <p className="text-xs text-slate-400  mt-1">Total credits</p>
                </CardContent>
              </Card>
            </div>

            {/* Organization Details */}
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="text-cyan-400 ">
                  Organization Details
                </CardTitle>
                <CardDescription className="text-slate-400 ">
                  General information about your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <label className="text-sm font-medium text-slate-400 ">
                        Name
                      </label>
                      <p className="text-sm text-cyan-100  mt-1">
                        {orgQuery.data.name}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-400 ">
                        Billing Email
                      </label>
                      <p className="text-sm text-cyan-100  mt-1">
                        {orgQuery.data.email}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="text-sm font-medium text-slate-400 ">
                        Updated
                      </label>
                      <p className="text-sm text-cyan-100  mt-1">
                        {new Date(orgQuery.data.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div></div>
                    <div>
                      <label className="text-sm font-medium text-slate-400 ">
                        Created
                      </label>
                      <p className="text-sm text-cyan-100  mt-1">
                        {new Date(orgQuery.data.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members" className="space-y-8">
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="text-cyan-400 ">Team Members</CardTitle>
                <CardDescription className="text-slate-400 ">
                  Manage your organization's team members and their roles
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700/50">
                      <TableHead className="text-slate-400 ">Name</TableHead>
                      <TableHead className="text-slate-400 ">Email</TableHead>
                      <TableHead className="text-slate-400 ">Role</TableHead>
                      <TableHead className="text-slate-400 ">Joined</TableHead>
                      <TableHead className="text-slate-400 ">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgMembersQuery.data.map((m) => {
                      return (
                        <TableRow className="border-slate-700/50">
                          <TableCell className="font-medium text-cyan-100 ">
                            {m.name}
                          </TableCell>
                          <TableCell className="text-slate-300 ">
                            {m.email}
                          </TableCell>
                          <TableCell>
                            <Badge className="text-yellow-400 bg-yellow-400/10 border-yellow-400/30 ">
                              {m.roles?.map((r) => r.toUpperCase()).join(",")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300 ">
                            {new Date(m.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge className="text-green-400 bg-green-400/10 border-green-400/30 ">
                              Active
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-8">
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="text-cyan-400 ">
                  Organization Settings
                </CardTitle>
                <CardDescription className="text-slate-400 ">
                  Configure your organization preferences and integrations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  <div>
                    <h4 className="font-medium mb-4 text-cyan-300 ">
                      GitHub Integration
                    </h4>
                    <div className="flex items-center justify-between p-6 border border-cyan-500/30 rounded-lg bg-slate-800/30">
                      <div className="flex items-center gap-4">
                        <Github className="h-8 w-8 text-cyan-400" />
                        <div>
                          <p className="font-medium text-cyan-100 ">
                            GitHub Organizations
                          </p>
                          <p className="text-sm text-slate-400 ">
                            Connected to GitHub for repository access
                          </p>
                        </div>
                      </div>
                      <Badge className="text-green-400 bg-green-400/10 border-green-400/30 ">
                        Connected
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
